import { useEffect, useMemo, useState } from "react";
import publicApi from "../api/publicClient";
import PublicLocationPickerModal from "../components/public/PublicLocationPickerModal";

/**
 * PublicMigrationRequestPage
 *
 * Stand-alone, unauthenticated page where subscribers can submit a
 * migration request after verifying their email via OTP.
 *
 * IMPORTANT: this page is intentionally rendered OUTSIDE the
 * authenticated DMS layout (AuthProvider, navbars, sidebars). It must
 * not depend on or render any internal admin UI.
 */

const STEP_EMAIL = "email";
const STEP_OTP = "otp";
const STEP_FORM = "form";
const STEP_SUCCESS = "success";

const EMAIL_RE = /^[A-Za-z0-9._%+\-]+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,}$/;

const VERIFY_STORAGE_KEY = "bestPublicMigrationVerify_v1";

function loadStoredVerification() {
  try {
    const raw = sessionStorage.getItem(VERIFY_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.email || !parsed?.verification_token || !parsed?.expires_at) return null;
    if (Date.now() >= parsed.expires_at) {
      sessionStorage.removeItem(VERIFY_STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function saveStoredVerification(email, token, ttlSeconds) {
  try {
    sessionStorage.setItem(
      VERIFY_STORAGE_KEY,
      JSON.stringify({
        email,
        verification_token: token,
        expires_at: Date.now() + (ttlSeconds || 30 * 60) * 1000,
      })
    );
  } catch {
    /* ignore */
  }
}

function clearStoredVerification() {
  try {
    sessionStorage.removeItem(VERIFY_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

function classNames(...xs) {
  return xs.filter(Boolean).join(" ");
}

function safeErrorMessage(err, fallback) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string" && detail.length < 200) return detail;
  return fallback;
}

function FieldLabel({ children, required }) {
  return (
    <label className="block text-xs font-semibold text-gray-700 mb-1">
      {children} {required && <span className="text-red-500">*</span>}
    </label>
  );
}

function SkyTruFiberLogo({ className = "" }) {
  return (
    <img
      src="/sky-trufiber-logo.png"
      alt="Sky TruFiber"
      className={className}
    />
  );
}

export default function PublicMigrationRequestPage() {
  // Override tab title and favicon for the public Sky TruFiber page.
  useEffect(() => {
    const prevTitle = document.title;
    document.title = "Sky Migration";

    const head = document.head;
    const iconSelectors = ['link[rel="icon"]', 'link[rel="apple-touch-icon"]'];
    const previousIcons = [];
    iconSelectors.forEach((sel) => {
      head.querySelectorAll(sel).forEach((el) => {
        previousIcons.push({ el, href: el.getAttribute("href") });
        el.setAttribute("href", "/sky-trufiber-logo.png");
      });
    });

    return () => {
      document.title = prevTitle;
      previousIcons.forEach(({ el, href }) => {
        if (href === null) el.removeAttribute("href");
        else el.setAttribute("href", href);
      });
    };
  }, []);

  const stored = typeof window !== "undefined" ? loadStoredVerification() : null;
  const [step, setStep] = useState(stored ? STEP_FORM : STEP_EMAIL);
  const [email, setEmail] = useState(stored?.email || "");
  const [verificationToken, setVerificationToken] = useState(stored?.verification_token || "");
  const [options, setOptions] = useState({
    municipalities: [],
    barangays_by_municipality: {},
    bundle_types: [],
  });

  // Step 1
  const [sendingOtp, setSendingOtp] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [statusMsg, setStatusMsg] = useState("");

  // Step 2
  const [otp, setOtp] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [otpError, setOtpError] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);
  const [otpExpiresIn, setOtpExpiresIn] = useState(0);

  // Step 3 - form
  const [form, setForm] = useState({
    old_account_number: "",
    name: "",
    municipality: "",
    barangay: "",
    address: "",
    is_condo: "",
    landmark: "",
    contact_number: "",
    bundle_type: "",
    latitude: null,
    longitude: null,
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [mapOpen, setMapOpen] = useState(false);

  // Step 4
  const [ticketNumber, setTicketNumber] = useState("");

  const setField = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  // Load dropdown options once.
  useEffect(() => {
    publicApi
      .get("/public/migration-request/options")
      .then((r) => setOptions(r.data || {}))
      .catch(() => {
        // Non-fatal — the user can still see the email step.
      });
  }, []);

  // Resend cooldown ticker.
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setInterval(() => setResendCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [resendCooldown]);

  // OTP expiry ticker (purely informational).
  useEffect(() => {
    if (otpExpiresIn <= 0) return;
    const t = setInterval(() => setOtpExpiresIn((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [otpExpiresIn]);

  // Reset barangay when municipality changes.
  useEffect(() => {
    setForm((f) => ({ ...f, barangay: "" }));
  }, [form.municipality]);

  // Clear a previously pinned location when the area changes — the old pin no
  // longer matches the newly selected municipality/barangay.
  useEffect(() => {
    setForm((f) => ({ ...f, latitude: null, longitude: null }));
  }, [form.municipality, form.barangay]);

  const barangayList = useMemo(() => {
    const m = (form.municipality || "").toUpperCase();
    return options.barangays_by_municipality?.[m] || [];
  }, [form.municipality, options.barangays_by_municipality]);

  const requiredOk =
    form.old_account_number.trim() &&
    form.name.trim() &&
    form.municipality &&
    form.barangay &&
    form.address.trim() &&
    (form.is_condo === "yes" || form.is_condo === "no") &&
    form.landmark.trim() &&
    form.contact_number.trim() &&
    form.bundle_type &&
    form.latitude != null &&
    form.longitude != null;

  async function handleSendOtp(isResend = false) {
    setEmailError("");
    setStatusMsg("");
    const e = email.trim().toLowerCase();
    if (!EMAIL_RE.test(e)) {
      setEmailError("Please enter a valid email address.");
      return;
    }
    setSendingOtp(true);
    try {
      const { data } = await publicApi.post("/public/migration-request/send-otp", {
        email: e,
      });
      setEmail(e);
      if (data?.cooldown_seconds) setResendCooldown(data.cooldown_seconds);
      if (data?.expires_in_seconds) setOtpExpiresIn(data.expires_in_seconds);
      setStatusMsg(data?.message || "OTP sent. Please check your email.");
      if (!isResend) setStep(STEP_OTP);
    } catch (err) {
      setEmailError(
        safeErrorMessage(err, "Unable to send OTP at this time. Please try again.")
      );
    } finally {
      setSendingOtp(false);
    }
  }

  async function handleVerifyOtp() {
    setOtpError("");
    const code = (otp || "").trim();
    if (!/^\d{4,10}$/.test(code)) {
      setOtpError("Enter the numeric code from your email.");
      return;
    }
    setVerifying(true);
    try {
      const { data } = await publicApi.post("/public/migration-request/verify-otp", {
        email,
        otp: code,
      });
      setVerificationToken(data.verification_token);
      saveStoredVerification(email, data.verification_token, data.token_expires_in_seconds);
      setStep(STEP_FORM);
    } catch (err) {
      setOtpError(
        safeErrorMessage(err, "Could not verify the OTP. Please try again.")
      );
    } finally {
      setVerifying(false);
    }
  }

  async function handleSubmit() {
    setSubmitError("");
    if (!requiredOk) return;
    setSubmitting(true);
    try {
      const payload = {
        email,
        verification_token: verificationToken,
        old_account_number: form.old_account_number.trim(),
        name: form.name.trim(),
        municipality: form.municipality,
        barangay: form.barangay,
        address: form.address.trim(),
        is_condo: form.is_condo === "yes",
        landmark: form.landmark.trim(),
        contact_number: form.contact_number.trim(),
        bundle_type: form.bundle_type,
        latitude: form.latitude,
        longitude: form.longitude,
      };
      const { data } = await publicApi.post(
        "/public/migration-request/submit",
        payload
      );
      setTicketNumber(data.ticket_number);
      clearStoredVerification();
      setStep(STEP_SUCCESS);
    } catch (err) {
      if (err?.response?.status === 401) {
        clearStoredVerification();
        setVerificationToken("");
        setStep(STEP_EMAIL);
        setEmailError("Your email verification has expired. Please verify again.");
      }
      setSubmitError(
        safeErrorMessage(
          err,
          "We couldn't submit your request. Please review your information and try again."
        )
      );
    } finally {
      setSubmitting(false);
    }
  }

  function handleStartOver() {
    clearStoredVerification();
    setStep(STEP_EMAIL);
    setEmail("");
    setOtp("");
    setVerificationToken("");
    setTicketNumber("");
    setStatusMsg("");
    setEmailError("");
    setOtpError("");
    setSubmitError("");
    setForm({
      old_account_number: "",
      name: "",
      municipality: "",
      barangay: "",
      address: "",
      is_condo: "",
      landmark: "",
      contact_number: "",
      bundle_type: "",
      latitude: null,
      longitude: null,
    });
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex flex-col">
      <header className="bg-white shadow-sm">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-4">
          <SkyTruFiberLogo className="h-12 sm:h-14 w-auto shrink-0" />
          <div className="border-l border-gray-200 pl-4">
            <div className="text-base sm:text-lg font-bold text-gray-800 leading-tight">
              Sky TruFiber Migration Request
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 px-4 py-6 sm:py-10">
        <div className="max-w-3xl mx-auto bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-5 sm:px-8 py-6 border-b">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-800">
              Migration Request Form
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Please verify your email first before submitting your migration request.
            </p>
            <Stepper step={step} />
          </div>

          <div className="px-5 sm:px-8 py-6">
            {step === STEP_EMAIL && (
              <EmailStep
                email={email}
                setEmail={setEmail}
                sendingOtp={sendingOtp}
                emailError={emailError}
                statusMsg={statusMsg}
                onSend={() => handleSendOtp(false)}
              />
            )}

            {step === STEP_OTP && (
              <OtpStep
                email={email}
                otp={otp}
                setOtp={setOtp}
                verifying={verifying}
                otpError={otpError}
                statusMsg={statusMsg}
                otpExpiresIn={otpExpiresIn}
                resendCooldown={resendCooldown}
                onVerify={handleVerifyOtp}
                onResend={() => handleSendOtp(true)}
                onChangeEmail={() => setStep(STEP_EMAIL)}
              />
            )}

            {step === STEP_FORM && (
              <FormStep
                email={email}
                form={form}
                setField={setField}
                setForm={setForm}
                options={options}
                barangayList={barangayList}
                requiredOk={!!requiredOk}
                submitting={submitting}
                submitError={submitError}
                onSubmit={handleSubmit}
                onOpenMap={() => setMapOpen(true)}
              />
            )}

            {step === STEP_SUCCESS && (
              <SuccessStep
                ticketNumber={ticketNumber}
                email={email}
                onStartOver={handleStartOver}
              />
            )}
          </div>
        </div>

        <p className="max-w-3xl mx-auto text-center text-xs text-gray-400 mt-6">
          Do not share this page or your OTP code with anyone. This form is
          provided for migration intake only.
        </p>
      </main>

      <PublicLocationPickerModal
        open={mapOpen}
        initial={
          form.latitude != null && form.longitude != null
            ? { lat: form.latitude, lng: form.longitude }
            : null
        }
        municipality={form.municipality}
        barangay={form.barangay}
        address={form.address}
        onConfirm={({ lat, lng }) => {
          setForm((f) => ({ ...f, latitude: lat, longitude: lng }));
          setMapOpen(false);
        }}
        onClose={() => setMapOpen(false)}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------
function Stepper({ step }) {
  const items = [
    { id: STEP_EMAIL, label: "Email" },
    { id: STEP_OTP, label: "Verify" },
    { id: STEP_FORM, label: "Details" },
    { id: STEP_SUCCESS, label: "Done" },
  ];
  const idx = items.findIndex((i) => i.id === step);
  return (
    <div className="flex items-center gap-2 mt-4">
      {items.map((it, i) => (
        <div key={it.id} className="flex items-center gap-2 flex-1">
          <div
            className={classNames(
              "w-7 h-7 rounded-full text-xs font-semibold flex items-center justify-center",
              i < idx
                ? "bg-green-500 text-white"
                : i === idx
                ? "bg-blue-600 text-white"
                : "bg-gray-200 text-gray-500"
            )}
          >
            {i + 1}
          </div>
          <div
            className={classNames(
              "text-xs",
              i <= idx ? "text-gray-700 font-medium" : "text-gray-400"
            )}
          >
            {it.label}
          </div>
          {i < items.length - 1 && (
            <div
              className={classNames(
                "h-0.5 flex-1 ml-1",
                i < idx ? "bg-green-400" : "bg-gray-200"
              )}
            />
          )}
        </div>
      ))}
    </div>
  );
}

function EmailStep({ email, setEmail, sendingOtp, emailError, statusMsg, onSend }) {
  return (
    <div className="space-y-4 max-w-md">
      <div>
        <FieldLabel required>Email Address</FieldLabel>
        <input
          type="email"
          autoComplete="email"
          inputMode="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full border rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder="you@example.com"
        />
        {emailError && <p className="text-xs text-red-600 mt-1">{emailError}</p>}
        {statusMsg && !emailError && (
          <p className="text-xs text-green-700 mt-1">{statusMsg}</p>
        )}
      </div>
      <button
        type="button"
        onClick={onSend}
        disabled={sendingOtp || !email.trim()}
        className="w-full sm:w-auto px-5 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold disabled:opacity-50"
      >
        {sendingOtp ? "Sending..." : "Verify Email"}
      </button>
      <p className="text-xs text-gray-500">
        We will send a 6-digit verification code to your email. The code is
        valid for 5 minutes.
      </p>
    </div>
  );
}

function OtpStep({
  email,
  otp,
  setOtp,
  verifying,
  otpError,
  statusMsg,
  otpExpiresIn,
  resendCooldown,
  onVerify,
  onResend,
  onChangeEmail,
}) {
  const mins = Math.floor(otpExpiresIn / 60);
  const secs = otpExpiresIn % 60;
  return (
    <div className="space-y-4 max-w-md">
      <div className="text-sm text-gray-600">
        We sent a verification code to <span className="font-semibold">{email}</span>.{" "}
        <button
          type="button"
          onClick={onChangeEmail}
          className="text-blue-600 hover:underline"
        >
          Change email
        </button>
      </div>
      <div>
        <FieldLabel required>One-Time Password</FieldLabel>
        <input
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={10}
          value={otp}
          onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
          className="w-full border rounded-lg px-3 py-2.5 text-base tracking-widest font-mono placeholder:text-gray-300 placeholder:font-sans placeholder:tracking-normal placeholder:text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder="Enter the code from your email"
        />
        {otpError && <p className="text-xs text-red-600 mt-1">{otpError}</p>}
        {!otpError && statusMsg && (
          <p className="text-xs text-green-700 mt-1">{statusMsg}</p>
        )}
        {otpExpiresIn > 0 && (
          <p className="text-xs text-gray-500 mt-1">
            OTP expires in {mins}:{String(secs).padStart(2, "0")}
          </p>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={onVerify}
          disabled={verifying || !otp.trim()}
          className="px-5 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold disabled:opacity-50"
        >
          {verifying ? "Verifying..." : "Verify OTP"}
        </button>
        <button
          type="button"
          onClick={onResend}
          disabled={resendCooldown > 0}
          className="px-5 py-2.5 rounded-lg border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-semibold disabled:opacity-50"
        >
          {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend OTP"}
        </button>
      </div>
    </div>
  );
}

function FormStep({
  email,
  form,
  setField,
  setForm,
  options,
  barangayList,
  requiredOk,
  submitting,
  submitError,
  onSubmit,
  onOpenMap,
}) {
  const muniList = options.municipalities || [];
  const bundleList = options.bundle_types || [];
  const locationReady =
    !!form.municipality && !!form.barangay && !!form.address.trim();
  const hasPin = form.latitude != null && form.longitude != null;
  return (
    <div className="space-y-4">
      <div className="bg-green-50 border border-green-200 text-green-800 text-sm rounded-lg px-3 py-2">
        Email verified: <span className="font-semibold">{email}</span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <FieldLabel required>Email Address</FieldLabel>
          <input
            value={email}
            readOnly
            className="w-full border rounded-lg px-3 py-2.5 text-sm bg-gray-100 text-gray-600"
          />
        </div>
        <div>
          <FieldLabel required>Account Number</FieldLabel>
          <input
            value={form.old_account_number}
            onChange={setField("old_account_number")}
            className="w-full border rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="e.g. 679010301"
          />
        </div>
      </div>

      <div>
        <FieldLabel required>Subscriber Name</FieldLabel>
        <input
          value={form.name}
          onChange={(e) =>
            setForm((f) => ({ ...f, name: e.target.value.toUpperCase() }))
          }
          className="w-full border rounded-lg px-3 py-2.5 text-sm uppercase focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder="LAST, FIRST M"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <FieldLabel required>City / Municipality</FieldLabel>
          <select
            value={form.municipality}
            onChange={setField("municipality")}
            className="w-full border rounded-lg px-3 py-2.5 text-sm bg-white"
          >
            <option value="">Select City / Municipality</option>
            {muniList.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </div>
        <div>
          <FieldLabel required>Barangay</FieldLabel>
          <select
            value={form.barangay}
            onChange={setField("barangay")}
            disabled={!form.municipality}
            className="w-full border rounded-lg px-3 py-2.5 text-sm bg-white disabled:bg-gray-100 disabled:text-gray-400"
          >
            <option value="">Select Barangay</option>
            {barangayList.map((b) => (
              <option key={b} value={b}>
                {b}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <FieldLabel required>Complete Address</FieldLabel>
        <input
          value={form.address}
          onChange={setField("address")}
          className="w-full border rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder="House/Unit No., Street, Subdivision"
        />
      </div>

      <div>
        <FieldLabel required>Pin Exact House Location</FieldLabel>
        <button
          type="button"
          onClick={onOpenMap}
          disabled={!locationReady}
          className={classNames(
            "w-full flex items-center justify-center gap-2 rounded-lg px-3 py-2.5 text-sm font-semibold border transition",
            !locationReady
              ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
              : hasPin
              ? "bg-green-50 text-green-700 border-green-300 hover:bg-green-100"
              : "bg-blue-600 text-white border-blue-600 hover:bg-blue-700"
          )}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a2 2 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          {hasPin ? "Location Pinned — Tap to Adjust" : "Open Map & Pin My Location"}
        </button>
        {!locationReady ? (
          <p className="text-xs text-gray-400 mt-1">
            Select your City / Municipality, Barangay and enter your Complete
            Address first to enable the map.
          </p>
        ) : (
          <p className="text-xs text-gray-500 mt-1">
            Open the satellite map and drop a pin on your exact house so our team
            can locate you accurately.
          </p>
        )}

        {hasPin && (
          <div className="grid grid-cols-2 gap-3 mt-3">
            <div>
              <FieldLabel>Latitude</FieldLabel>
              <input
                value={form.latitude.toFixed(6)}
                readOnly
                className="w-full border rounded-lg px-3 py-2.5 text-sm bg-gray-100 text-gray-600 font-mono"
              />
            </div>
            <div>
              <FieldLabel>Longitude</FieldLabel>
              <input
                value={form.longitude.toFixed(6)}
                readOnly
                className="w-full border rounded-lg px-3 py-2.5 text-sm bg-gray-100 text-gray-600 font-mono"
              />
            </div>
          </div>
        )}
      </div>

      <div>
        <FieldLabel required>Is this a Condo Unit?</FieldLabel>
        <div className="flex items-center gap-6 pt-1">
          <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="radio"
              name="is_condo"
              value="yes"
              checked={form.is_condo === "yes"}
              onChange={() => setForm((f) => ({ ...f, is_condo: "yes" }))}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500"
            />
            <span>Yes</span>
          </label>
          <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="radio"
              name="is_condo"
              value="no"
              checked={form.is_condo === "no"}
              onChange={() => setForm((f) => ({ ...f, is_condo: "no" }))}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500"
            />
            <span>No</span>
          </label>
        </div>
      </div>

      <div>
        <FieldLabel required>Nearest Landmark, Contact Person and Additional Instruction</FieldLabel>
        <textarea
          value={form.landmark}
          onChange={setField("landmark")}
          rows={2}
          className="w-full border rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder="e.g. Near 7-Eleven, blue gate, ring twice"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <FieldLabel required>Contact Number</FieldLabel>
          <input
            value={form.contact_number}
            onChange={setField("contact_number")}
            className="w-full border rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Mobile or landline"
            inputMode="tel"
          />
        </div>
        <div>
          <FieldLabel required>Bundle Type</FieldLabel>
          <select
            value={form.bundle_type}
            onChange={setField("bundle_type")}
            className="w-full border rounded-lg px-3 py-2.5 text-sm bg-white"
          >
            <option value="">Select Bundle Type</option>
            {bundleList.map((bt) => (
              <option key={bt} value={bt}>
                {bt}
              </option>
            ))}
          </select>
        </div>
      </div>

      {form.bundle_type === "Cable Only" && (
        <div className="bg-amber-50 border border-amber-300 rounded-lg px-4 py-3 text-sm">
          <div className="font-semibold text-amber-900 mb-1">Cable Only Migration Notice:</div>
          <p className="text-amber-800 leading-relaxed">
            Your existing digibox will be migrated to an IPTV box. Since IPTV
            requires internet connection, we will install a new fiber modem with
            15 Mbps speed. An additional ₱500 per month will be added to your
            bill on top of your Cable/IPTV subscription.
          </p>
        </div>
      )}

      {submitError && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-3 py-2">
          {submitError}
        </div>
      )}

      <div className="pt-2">
        <button
          type="button"
          onClick={onSubmit}
          disabled={!requiredOk || submitting}
          className="w-full sm:w-auto px-6 py-3 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold disabled:opacity-50"
        >
          {submitting ? "Submitting..." : "Submit Migration Request"}
        </button>
        <p className="text-xs text-gray-500 mt-2">
          Required fields are marked with <span className="text-red-500">*</span>.
        </p>
      </div>
    </div>
  );
}

function SuccessStep({ ticketNumber, email, onStartOver }) {
  return (
    <div className="space-y-5">
      <div className="bg-green-50 border border-green-200 rounded-lg p-5 text-center">
        <div className="text-green-700 text-3xl mb-2">✓</div>
        <h2 className="text-lg font-bold text-gray-800">
          Your migration request has been submitted successfully.
        </h2>
        <p className="text-sm text-gray-600 mt-1">
          A copy of your ticket number has been sent to{" "}
          <span className="font-semibold">{email}</span>.
        </p>
        <div className="mt-4">
          <div className="text-xs text-gray-500">Your ticket number</div>
          <div className="text-xl sm:text-2xl font-mono font-bold tracking-wider text-blue-700 mt-1">
            {ticketNumber}
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-3">
          Please save this ticket number for your reference.
        </p>
      </div>
    </div>
  );
}
